import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/ui/LanguageSwitcher';
import { ColorSchemeToggle } from './ColorSchemeToggle';
import { PromoBanner } from '../../components/PromoBanner';
import { GlobalAnnouncementBanner } from '../../components/GlobalAnnouncementBanner';

export function LandingNav({ homePath = '/' }: { homePath?: string } = {}) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const anchorLinks = [
    { to: `${homePath}#courses`, labelKey: 'landing.nav.courses' },
    { to: `${homePath}#teachers`, labelKey: 'landing.nav.teachers' },
    { to: `${homePath}#pricing`, labelKey: 'landing.nav.pricing' },
  ];

  const routeLinks = [
    { to: '/blog', labelKey: 'landing.nav.blog' },
    { to: '/contact', labelKey: 'landing.nav.contact' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-manuscript-line bg-paper-100 font-manuscript">
      <GlobalAnnouncementBanner />
      <PromoBanner />
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
        <Link to={homePath} className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          <Link
            to={homePath}
            className="text-sm font-medium text-manuscript-ink-soft transition-colors hover:text-manuscript-ink"
          >
            {t('landing.nav.home')}
          </Link>
          {anchorLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-medium text-manuscript-ink-soft transition-colors hover:text-manuscript-ink"
            >
              {t(link.labelKey)}
            </Link>
          ))}
          {routeLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-medium text-manuscript-ink-soft transition-colors hover:text-manuscript-ink"
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <ColorSchemeToggle />
          <LanguageSwitcher />
          <Link to="/login" className="text-sm font-medium text-manuscript-ink-soft hover:text-manuscript-ink">
            {t('landing.nav.login')}
          </Link>
          <Link
            to="/essai-gratuit"
            className="rounded-sm bg-seal-500 px-5 py-2.5 text-sm font-semibold text-paper-50 shadow-sm transition-colors hover:bg-seal-400"
          >
            {t('landing.nav.trialCta')}
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="rounded-lg p-2 text-manuscript-ink lg:hidden"
          aria-label={t('landing.nav.menu')}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {isMenuOpen && (
        <div className="border-t border-manuscript-line bg-paper-100 px-6 py-4 lg:hidden">
          <nav className="flex flex-col gap-3">
            <Link
              to={homePath}
              onClick={() => setIsMenuOpen(false)}
              className="text-sm font-medium text-manuscript-ink-soft"
            >
              {t('landing.nav.home')}
            </Link>
            {anchorLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-manuscript-ink-soft"
              >
                {t(link.labelKey)}
              </Link>
            ))}
            {routeLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className="text-sm font-medium text-manuscript-ink-soft"
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link to="/login" className="text-sm font-medium text-manuscript-ink-soft">
              {t('landing.nav.login')}
            </Link>
            <div className="flex items-center gap-3">
              <ColorSchemeToggle />
              <LanguageSwitcher className="self-start" />
            </div>
            <Link
              to="/essai-gratuit"
              onClick={() => setIsMenuOpen(false)}
              className="mt-1 rounded-sm bg-seal-500 px-5 py-2.5 text-center text-sm font-semibold text-paper-50"
            >
              {t('landing.nav.trialCta')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
